/**
 * CTRL_09_ConfigPanel - Data Residency & Sovereignty Configuration
 */
import React, { useState, useEffect } from 'react';
import { Card, Switch, Button, Collapse, Select, Tag, Alert, Spin } from 'antd';
import {
  SettingOutlined,
  CheckCircleOutlined,
  WarningOutlined,
  SaveOutlined
} from '@ant-design/icons';
import { getControlConfig, saveControlConfig } from '../api/trustgridApi';
import { showError, showSuccess } from '../../../utils/notifications';

const { Panel } = Collapse;
const { Option } = Select;

// AWS/Cloud regions
const CLOUD_REGIONS = [
  { value: 'us-east-1', label: 'US East (N. Virginia)', jurisdiction: 'US' },
  { value: 'us-east-2', label: 'US East (Ohio)', jurisdiction: 'US' },
  { value: 'us-west-1', label: 'US West (N. California)', jurisdiction: 'US' },
  { value: 'us-west-2', label: 'US West (Oregon)', jurisdiction: 'US' },
  { value: 'eu-west-1', label: 'EU (Ireland)', jurisdiction: 'EU' },
  { value: 'eu-west-2', label: 'EU (London)', jurisdiction: 'UK' },
  { value: 'eu-west-3', label: 'EU (Paris)', jurisdiction: 'EU' },
  { value: 'eu-central-1', label: 'EU (Frankfurt)', jurisdiction: 'EU' },
  { value: 'eu-north-1', label: 'EU (Stockholm)', jurisdiction: 'EU' },
  { value: 'ap-southeast-1', label: 'Asia Pacific (Singapore)', jurisdiction: 'APAC' },
  { value: 'ap-southeast-2', label: 'Asia Pacific (Sydney)', jurisdiction: 'APAC' },
  { value: 'ap-northeast-1', label: 'Asia Pacific (Tokyo)', jurisdiction: 'APAC' },
  { value: 'ap-northeast-2', label: 'Asia Pacific (Seoul)', jurisdiction: 'APAC' },
  { value: 'ap-south-1', label: 'Asia Pacific (Mumbai)', jurisdiction: 'APAC' },
  { value: 'ca-central-1', label: 'Canada (Central)', jurisdiction: 'CA' },
  { value: 'sa-east-1', label: 'South America (São Paulo)', jurisdiction: 'SA' }
];

function CTRL_09_ConfigPanel({ projectId, controlCode, project }) {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [config, setConfig] = useState(null);
  const [hasChanges, setHasChanges] = useState(false);
  const [activeKeys, setActiveKeys] = useState(['1']);

  useEffect(() => {
    if (projectId && controlCode) {
      fetchConfig();
    }
  }, [projectId, controlCode]);

  const fetchConfig = async () => {
    setLoading(true);
    try {
      const response = await getControlConfig(projectId, controlCode);
      setConfig(response.data);
    } catch (error) {
      console.error('Failed to fetch config:', error);
      setConfig({
        enabled: false,
        config: {
          allowed_regions: [],
          block_cross_border_transfer: false,
          require_sccs: true
        }
      });
      if (error.response && error.response.status !== 404) {
        showError('Failed to load configuration. Using defaults.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await saveControlConfig(projectId, controlCode, config);
      showSuccess('Configuration saved successfully');
      setHasChanges(false);
      fetchConfig();
    } catch (error) {
      console.error('Failed to save config:', error);
      showError(error);
    } finally {
      setSaving(false);
    }
  };

  const updateConfig = (field, value) => {
    setConfig({
      ...config,
      config: {
        ...config.config,
        [field]: value
      }
    });
    setHasChanges(true);
  };

  if (loading) {
    return (
      <Card>
        <div style={{ display: 'flex', justifyContent: 'center', padding: '40px' }}>
          <Spin size="large" />
        </div>
      </Card>
    );
  }

  if (!config) return null;

  const configData = config.config || {};
  const isConfigured = config.enabled && configData.allowed_regions?.length > 0;
  const statusIcon = isConfigured ? <CheckCircleOutlined style={{ color: '#52c41a' }} /> : <WarningOutlined style={{ color: '#faad14' }} />;
  const statusText = isConfigured ? 'Configured' : 'Setup Required';

  return (
    <Card
      title={
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <SettingOutlined />
          <span>CTRL_09 Configuration: Data Residency & Sovereignty</span>
          {statusIcon}
          <Tag color={isConfigured ? 'green' : 'orange'}>{statusText}</Tag>
        </div>
      }
      extra={
        <Button
          type="primary"
          icon={<SaveOutlined />}
          onClick={handleSave}
          loading={saving}
          disabled={!hasChanges}
        >
          Save Changes
        </Button>
      }
    >
      <Collapse activeKey={activeKeys} onChange={setActiveKeys} expandIconPosition="right">
        <Panel
          header={
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <strong>1. Enable Data Residency Controls</strong>
              {config.enabled && <Tag color="green">Active</Tag>}
            </div>
          }
          key="1"
        >
          <div style={{ padding: '16px 0' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '16px' }}>
              <Switch
                checked={config.enabled}
                onChange={(checked) => setConfig({ ...config, enabled: checked })}
                size="large"
              />
              <div>
                <div style={{ fontWeight: 'bold', marginBottom: '4px' }}>
                  {config.enabled ? 'Residency Controls Enabled' : 'Residency Controls Disabled'}
                </div>
                <div style={{ color: '#888', fontSize: '13px' }}>
                  {config.enabled
                    ? 'Monitoring data storage locations and cross-border transfers'
                    : 'Enable to enforce data residency and sovereignty requirements'}
                </div>
              </div>
            </div>

            {!config.enabled && (
              <Alert
                type="info"
                message="About Data Residency & Sovereignty"
                description="This control ensures data is stored in authorized regions and cross-border transfers comply with regulations like GDPR, data localization laws, and Standard Contractual Clauses (SCCs)."
                showIcon
                style={{ marginTop: '16px' }}
              />
            )}
          </div>
        </Panel>

        <Panel
          header={
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <strong>2. Advanced Configuration</strong>
              {configData.allowed_regions?.length > 0 && <Tag color="blue">{configData.allowed_regions.length} regions</Tag>}
            </div>
          }
          key="2"
          disabled={!config.enabled}
        >
          <div style={{ padding: '16px 0' }}>
            <div style={{ marginBottom: '24px' }}>
              <label style={{ display: 'block', fontWeight: 'bold', marginBottom: '8px' }}>
                Allowed Regions
                <span style={{ fontWeight: 'normal', color: '#888', marginLeft: '8px' }}>
                  ({configData.allowed_regions?.length || 0} selected)
                </span>
              </label>
              <Select
                mode="multiple"
                value={configData.allowed_regions || []}
                onChange={(value) => updateConfig('allowed_regions', value)}
                style={{ width: '100%', marginBottom: '8px' }}
                placeholder="Select authorized regions for data storage"
                optionFilterProp="children"
              >
                {CLOUD_REGIONS.map(region => (
                  <Option key={region.value} value={region.value}>
                    <div>
                      <Tag color="blue" style={{ marginRight: '8px' }}>{region.jurisdiction}</Tag>
                      {region.label}
                    </div>
                  </Option>
                ))}
              </Select>
              <div style={{ fontSize: '12px', color: '#888' }}>
                Select cloud regions where data storage is authorized. Events with data in unauthorized regions will trigger violations.
              </div>
              {configData.allowed_regions?.length === 0 && (
                <Alert
                  type="warning"
                  message="No regions selected"
                  description="You must select at least one authorized region to enable residency enforcement."
                  showIcon
                  style={{ marginTop: '12px' }}
                />
              )}
            </div>

            <div style={{ marginBottom: '24px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <Switch
                  checked={configData.block_cross_border_transfer ?? false}
                  onChange={(checked) => updateConfig('block_cross_border_transfer', checked)}
                />
                <label style={{ fontWeight: 'bold' }}>Block Cross-Border Transfers</label>
              </div>
              <div style={{ fontSize: '12px', color: '#888', marginTop: '4px', marginLeft: '44px' }}>
                Prevent data transfers between different jurisdictions (e.g., US → EU)
              </div>
            </div>

            <div style={{ marginBottom: '24px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <Switch
                  checked={configData.require_sccs ?? true}
                  onChange={(checked) => updateConfig('require_sccs', checked)}
                />
                <label style={{ fontWeight: 'bold' }}>Require Standard Contractual Clauses (SCCs)</label>
              </div>
              <div style={{ fontSize: '12px', color: '#888', marginTop: '4px', marginLeft: '44px' }}>
                Require SCCs for EU data transfers to non-adequate jurisdictions (GDPR Art. 46)
              </div>
            </div>

            <Alert
              type="success"
              message="Configuration Summary"
              description={
                <div>
                  <p><strong>Allowed Regions:</strong> {configData.allowed_regions?.length > 0 ? configData.allowed_regions.join(', ') : 'None selected'}</p>
                  <p><strong>Block Cross-Border:</strong> {configData.block_cross_border_transfer ? 'Yes' : 'No'}</p>
                  <p><strong>Require SCCs:</strong> {configData.require_sccs ? 'Yes' : 'No'}</p>
                </div>
              }
              showIcon
              style={{ marginTop: '16px' }}
            />
          </div>
        </Panel>
      </Collapse>
    </Card>
  );
}

export default CTRL_09_ConfigPanel;
